import './utils/monitoring';
import { initGTM } from './utils/gtm';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  Navigate,
  RouterProvider,
  Outlet,
  useRouteError,
} from 'react-router-dom';
import { Provider, useDispatch } from 'react-redux';
import store, { AppDispatch } from './app/store';
import { HelmetProvider } from 'react-helmet-async';
import { App as AntdApp } from 'antd';
import { GlobalApp } from './utils/globalApp';
import './styles/index.less';
import AgentApp from './AgentApp';
import App from './App';
import reportWebVitals from './reportWebVitals';
import ChangeRequestsScreen from './pages/ChangeRequests/ChangeRequestsScreen';
import NewChangeRequestScreen from './pages/ChangeRequests/NewChangeRequestScreen';
import ChangeRequestDetailScreen from './pages/ChangeRequestDetail/ChangeRequestDetailScreen';
import { CustomAuth0Provider } from './components/Login/CustomAuth0Provider';
import { AuthenticationGuard } from './components/Login/AuthenticationGuard';
import SignUp from './pages/sign-up';
import VerifyEmail from './pages/VerifyEmail/VerifyEmail';
import EmailVerified from './pages/EmailVerified/EmailVerified';
import EmailVerifiedAccountMismatch from './pages/EmailVerified/EmailVerifiedAccountMismatch';
import SignupFailed from './pages/SignupFailed/SignupFailed';
import AdminAgentScreen from './pages/AdminAgent/AdminAgentScreen';
import SubscriptionScreen from './pages/Subscription/SubscriptionScreen';
import SettingsScreen from './pages/Settings/SettingsScreen';
import RoleGuard from './guards/RoleGuard';
import UserRoles from './enums/userRoles';
import NotFound from './pages/NotFound/NotFound';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import PromptLibrary from './components/PromptLibrary/PromptLibrary';
import { fetchAppFeatures } from './app/Slices/appFeatureSlice';
import { ConfigProvider, theme as antdTheme } from 'antd';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import McpScreen from './pages/McpScreen/McpScreen';
import { CirraServiceType } from './enums/auth';
import PricingScreen from './pages/PricingScreen/PricingScreen';
import BuyCreditsScreen from './pages/BuyCreditsScreen/BuyCreditsScreen';
import TeamsScreen from './pages/TeamsScreen/TeamsScreen';
import UsersScreen from './pages/Users/UsersScreen';
import AcceptInvitation from './pages/AcceptInvitation/AcceptInvitation';
import { Z_INDEX_APP_ALERT } from './constants/zIndex';

const ErrorThrower = () => {
  const error = useRouteError();
  if (error) {
    throw error;
  }
  return null;
};

// Create a layout component for the public pages
const PublicLayout: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <div className="public-layout">{children}</div>;

const AppRouter: React.FC = () => {
  const [adminAgentEnabled, setAdminAgentEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    const fetchFeatures = async () => {
      const { adminAgentEnabled } = await dispatch(fetchAppFeatures()).unwrap();
      setAdminAgentEnabled(adminAgentEnabled);
      setIsLoading(false);
    };

    fetchFeatures();
  }, []);

  if (isLoading) {
    return <></>;
  }

  // Main MCP layout - this is now the primary application experience
  const mcpLayout = (
    <CustomAuth0Provider service={CirraServiceType.MCP}>
      <AuthenticationGuard component={App} />
    </CustomAuth0Provider>
  );

  // Agent-specific layout for admin agent and change requests
  const agentLayout = (
    <CustomAuth0Provider service={CirraServiceType.AGENTS}>
      <AuthenticationGuard component={AgentApp} />
    </CustomAuth0Provider>
  );

  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        <Route errorElement={<ErrorThrower />}>
          {/* Public routes - use MCP service for consistency */}
          <Route
            element={
              <CustomAuth0Provider service={CirraServiceType.MCP}>
                <PublicLayout>
                  <Outlet />
                </PublicLayout>
              </CustomAuth0Provider>
            }
          >
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/email-verified" element={<EmailVerified />} />
            <Route
              path="/email-verified-mismatch"
              element={<EmailVerifiedAccountMismatch />}
            />
            <Route path="/signup-failed" element={<SignupFailed />} />
            <Route path="/accept-invitation" element={<AcceptInvitation />} />
          </Route>

          {/* MCP Public routes */}
          <Route
            element={
              <CustomAuth0Provider service={CirraServiceType.MCP}>
                <PublicLayout>
                  <Outlet />
                </PublicLayout>
              </CustomAuth0Provider>
            }
          >
            <Route path="/sign-up" element={<SignUp />} />
            <Route
              path="/free-trial"
              element={<Navigate to="/sign-up" replace />}
            />
          </Route>

          {/* MCP is now the main application - root route */}
          <Route path="/" element={mcpLayout}>
            <Route index element={<McpScreen />} />
            <Route path="buy-credits" element={<BuyCreditsScreen />} />
          </Route>

          {/* Keep /mcp as an alias for backward compatibility */}
          <Route path="/mcp" element={<Navigate to="/" replace />} />

          {/* Pricing route - keep as standalone for direct access */}
          <Route
            path="/pricing"
            element={
              <CustomAuth0Provider service={CirraServiceType.MCP}>
                <AuthenticationGuard component={PricingScreen} />
              </CustomAuth0Provider>
            }
          />

          {/* Legacy agent routes - keep these as the primary Auth0 callback URLs */}
          <Route path="/admin-agent" element={agentLayout}>
            <Route index element={<AdminAgentScreen />} />
            {adminAgentEnabled && (
              <>
                <Route path="thread/:threadId" element={<AdminAgentScreen />} />
                <Route path="prompt-library" element={<PromptLibrary />} />
                <Route
                  element={
                    <RoleGuard
                      allowedRoles={[UserRoles.ADMIN]}
                      fallbackPath="/admin-agent"
                    />
                  }
                >
                  <Route path="settings" element={<SettingsScreen />} />
                  <Route path="subscription" element={<SubscriptionScreen />} />
                </Route>
              </>
            )}
          </Route>

          <Route path="/change-requests" element={agentLayout}>
            <Route index element={<ChangeRequestsScreen />} />
            <Route path="new" element={<NewChangeRequestScreen />} />
            <Route
              path=":changeRequestRouteId"
              element={<ChangeRequestDetailScreen />}
            />
          </Route>

          {/* Global settings pages */}
          <Route path="settings" element={mcpLayout}>
            <Route path="teams" element={<TeamsScreen />} />
            <Route
              path="teams/create"
              element={<TeamsScreen isCreatingTeam />}
            />
          </Route>

          {/* Team settings pages */}
          <Route path="teams/:teamId" element={mcpLayout}>
            <Route path="access/users" element={<UsersScreen />} />
          </Route>

          {/* Catch-all route for 404 */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </>
    )
  );

  return <RouterProvider router={router} />;
};

const MemoizedAppRouter = React.memo(AppRouter);

const AppWithTheme = () => {
  const { isDark } = useTheme();
  return (
    <ConfigProvider
      theme={{
        algorithm: isDark
          ? antdTheme.darkAlgorithm
          : antdTheme.defaultAlgorithm,
        components: {
          // Notification created by ApplicationAlert
          Notification: {
            zIndexPopup: Z_INDEX_APP_ALERT,
          },
        },
      }}
    >
      <MemoizedAppRouter />
    </ConfigProvider>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <AntdApp>
      <ErrorBoundary>
        <Provider store={store}>
          <HelmetProvider>
            <GlobalApp />
            <ThemeProvider>
              <AppWithTheme />
            </ThemeProvider>
          </HelmetProvider>
        </Provider>
      </ErrorBoundary>
    </AntdApp>
  </React.StrictMode>
);

// Load Google Tag Manager in production builds only
initGTM();

// Load Pingdom RUM tracking in production builds only
if (process.env.NODE_ENV === 'production') {
  // Use different Pingdom IDs based on target environment
  const PINGDOM_IDS: Record<string, string> = {
    prod: 'pa-680fed8f5c6a700012000707.js',
    dev: 'pa-6945ffd5940ae6001200000c.js',
  };

  const targetEnv = process.env.REACT_APP_API_BASE_URL?.includes('-dev.')
    ? 'dev'
    : 'prod';
  const pingdomId = PINGDOM_IDS[targetEnv];

  if (pingdomId) {
    try {
      const script = document.createElement('script');
      script.src = `//rum-static.pingdom.net/${pingdomId}`;
      script.async = true;
      script.onerror = () => {
        // Silently fail if script cannot be loaded
        console.warn(`Pingdom RUM script failed to load (${targetEnv})`);
      };
      document.body.appendChild(script);
    } catch (error) {
      // Silently fail if script injection fails
      console.warn(
        `Failed to inject Pingdom RUM script (${targetEnv}):`,
        error
      );
    }
  }
}
reportWebVitals();
